import { Image } from "expo-image";
import { useRef } from "react";
import { View } from "tamagui";

import {
  OutputContentList,
  OutputImageDynamicPart,
  OutputImageStaticPart,
  StaticAssetKey,
} from "@/api/generated/models";
import { AssistantClientEvent } from "@/api/model/Conversation";
import noriApproachImg from "@/assets/images/static/nori-approach.png";
import { AutoExpandingView } from "@/components/animated";
import LoadingSpinner from "@/components/animated/LoadingSpinner";
import { captureMessage } from "@/sentry";
import { BLUR_HASH } from "@/theme/constants";
import OutputText from "./OutputText";

const IMAGE_TRANSITION_MS = 300;
const ASSET_MAP = {
  [StaticAssetKey.nori_approach_phrase]: noriApproachImg,
} as const;

const OutputImageStatic = ({
  asset_key,
  alt_text,
  isLatest,
  messageId,
}: {
  isLatest: boolean;
  messageId: string;
} & OutputImageStaticPart) => {
  const imageSource = ASSET_MAP[asset_key];
  const hasLoggedMissingAsset = useRef(false);

  if (!imageSource) {
    if (!hasLoggedMissingAsset.current) {
      captureMessage("Static asset not found", {
        extra: { asset_key },
      });
      hasLoggedMissingAsset.current = true;
    }
    return null;
  }

  return (
    <AutoExpandingView
      animationId={messageId}
      shouldAnimate={isLatest}
      marginBottom="$200"
      borderRadius="$150"
      overflow="hidden"
      maxWidth={500}
    >
      <Image
        source={imageSource}
        style={{
          aspectRatio: 16 / 9,
        }}
        contentFit="cover"
        placeholder={{ blurhash: BLUR_HASH }}
        transition={IMAGE_TRANSITION_MS}
        alt={alt_text || ""}
        onError={(e) =>
          captureMessage(`Static asset failed to load: ${e?.error}`, {
            extra: { asset_key },
          })
        }
      />
    </AutoExpandingView>
  );
};

const OutputImageDynamic = ({
  image_id,
  status,
  url,
  isLatest,
  messageId,
}: {
  isLatest: boolean;
  messageId: string;
} & OutputImageDynamicPart) => {
  if (status === "failed") {
    return null;
  }

  return (
    <AutoExpandingView
      animationId={`${messageId}-${image_id}`}
      shouldAnimate={isLatest}
      marginBottom="$200"
      borderRadius="$150"
      overflow="hidden"
      maxWidth={500}
    >
      <Image
        source={url ? { uri: url, cacheKey: image_id } : undefined}
        style={{
          aspectRatio: 16 / 9,
        }}
        contentFit="cover"
        placeholder={{ blurhash: BLUR_HASH }}
        transition={url ? IMAGE_TRANSITION_MS : undefined}
      />
      {!url && (
        <View
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          alignItems="center"
          justifyContent="center"
        >
          <LoadingSpinner size="$600" />
        </View>
      )}
    </AutoExpandingView>
  );
};

export interface OutputContentProps extends AssistantClientEvent {
  content: OutputContentList;
  type: "output_content";
}

/**
 * Renders an array of OutputContentList items.
 * Note: Images are reordered to be rendered first, followed by text.
 */
const OutputContent = ({
  content,
  type,
  isLatest,
  ...props
}: OutputContentProps) => {
  const imageParts = content.filter(
    (part) =>
      part.type === "output_image_static" ||
      part.type === "output_image_dynamic",
  );
  const textParts = content.filter((part) => part.type === "output_text");

  return (
    <>
      {imageParts.map((part, index) => {
        if (part.type === "output_image_static") {
          return (
            <OutputImageStatic
              key={index}
              {...props}
              {...part}
              isLatest={isLatest}
              messageId={props.messageId}
            />
          );
        }
        if (part.type === "output_image_dynamic") {
          return (
            <OutputImageDynamic
              key={part.image_id}
              {...props}
              {...part}
              isLatest={isLatest}
              messageId={props.messageId}
            />
          );
        }
        return null;
      })}
      {textParts.map(({ type, text }, index) => {
        if (type === "output_text") {
          return (
            <OutputText
              key={index}
              {...props}
              isLatest={isLatest}
              type={type}
              content={text}
            />
          );
        }
        return null;
      })}
    </>
  );
};

export default OutputContent;
